import { DisplayObjectContainer } from './DisplayObjectContainer';
import { RenderKind } from './DisplayObject';
import type { InteractiveObject } from './InteractiveObject';
import type { LoaderInfo } from './LoaderInfo';
import { Event, FocusEvent } from '../events/Event';
import { Rectangle } from '../geom/Rectangle';
import { rt } from '../player/runtime';

/** A rectangle in stage coordinates (PORT: Stage.visibleRect / safeRect). */
export interface StageArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** flash.display.Stage (created by FlashPlayer). */
export class Stage extends DisplayObjectContainer {
  /** @internal */ $stageWidth: number;
  /** @internal */ $stageHeight: number;
  /** @internal */ $frameRate: number;
  /** @internal */ $focus: InteractiveObject | null;
  /** @internal */ $quality: string;
  /** @internal */ $scaleMode: string;
  /** @internal */ $align: string;
  /** @internal */ $displayState: string;
  /** @internal */ $showDefaultContextMenu: boolean;
  /** @internal */ $stageFocusRect: boolean;
  /** @internal */ $loaderInfo: LoaderInfo | null;
  /** @internal */ $invalidated: boolean;
  /** @internal */ $color: number;
  /** @internal PORT (wide screens): the part of the stage the screen shows; null = the stage rectangle. */
  $visibleArea: StageArea | null;
  /** @internal PORT: the part of $visibleArea clear of notches, rounded corners and system bars; null = all of it. */
  $safeArea: StageArea | null;
  /** @internal PORT: CSS px per stage px of what is shown (1: no host told it). */
  $cssScale: number;
  /**
   * @internal PORT: the host shows a focused text field above an on-screen keyboard (its view zoomed or lifted to the
   * field, not the screen's own): layouts that follow the shown area keep theirs meanwhile.
   */
  $keyboardView: boolean;

  constructor(width: number = 807, height: number = 600, frameRate: number = 50) {
    super();
    this.$name = null as any;
    this.$stageWidth = width;
    this.$stageHeight = height;
    this.$frameRate = frameRate;
    this.$focus = null;
    this.$quality = 'high';
    this.$scaleMode = 'showAll';
    this.$align = '';
    this.$displayState = 'normal';
    this.$showDefaultContextMenu = true;
    this.$stageFocusRect = true;
    this.$loaderInfo = null;
    this.$invalidated = false;
    this.$color = 0;
    this.$visibleArea = null;
    this.$safeArea = null;
    this.$cssScale = 1;
    this.$keyboardView = false;
  }

  /** @internal */ get $isStage(): boolean {
    return true;
  }
  /** @internal */ get $renderKind(): RenderKind {
    return 'stage';
  }

  get stageWidth(): number {
    return this.$stageWidth;
  }
  set stageWidth(_v: number) {
    /* read-only in practice (scaleMode noScale only) */
  }
  get stageHeight(): number {
    return this.$stageHeight;
  }
  set stageHeight(_v: number) {}
  get frameRate(): number {
    return this.$frameRate;
  }
  set frameRate(v: number) {
    const f = Math.min(1000, Math.max(0.01, +v || 0.01));
    this.$frameRate = f;
    rt().frameRate = f;
  }
  get color(): number {
    return this.$color;
  }
  set color(v: number) {
    this.$color = (v >>> 0) & 0xffffff;
  }

  get focus(): InteractiveObject | null {
    return this.$focus;
  }
  set focus(v: InteractiveObject | null) {
    const old = this.$focus;
    if (old === v) return;
    this.$focus = v ?? null;
    if (old) old.$dispatch(new FocusEvent(FocusEvent.FOCUS_OUT, true, false, v));
    if (v) {
      const tf: any = v;
      if (tf.$renderKind === 'text' && typeof tf.setSelection === 'function' && tf.$type === 'input') {
        tf.$selBegin = tf.$selEnd = tf.$spans.text.length;
      }
      v.$dispatch(new FocusEvent(FocusEvent.FOCUS_IN, true, false, old));
    }
  }

  get quality(): string {
    return this.$quality;
  }
  set quality(v: string) {
    this.$quality = String(v).toLowerCase();
  }
  get scaleMode(): string {
    return this.$scaleMode;
  }
  set scaleMode(v: string) {
    this.$scaleMode = String(v);
  }
  get align(): string {
    return this.$align;
  }
  set align(v: string) {
    this.$align = String(v).toUpperCase();
  }
  get displayState(): string {
    return this.$displayState;
  }
  set displayState(v: string) {
    this.$displayState = String(v);
  }
  get showDefaultContextMenu(): boolean {
    return this.$showDefaultContextMenu;
  }
  set showDefaultContextMenu(v: boolean) {
    this.$showDefaultContextMenu = !!v;
  }
  get stageFocusRect(): boolean {
    return this.$stageFocusRect;
  }
  set stageFocusRect(v: boolean) {
    this.$stageFocusRect = !!v;
  }
  get fullScreenWidth(): number {
    return this.$stageWidth;
  }
  get fullScreenHeight(): number {
    return this.$stageHeight;
  }

  /**
   * PORT (not Flash): the part of the stage the screen shows, in stage coordinates. Flash's showAll draws the whole
   * stage and, on a screen of another shape, whatever lies beyond its edges; a wide host (BrowserHost `wide`) shows
   * such a margin instead of letterbox bars. Without one it is the stage rectangle (0, 0, stageWidth, stageHeight),
   * as in a headless player.
   */
  get visibleRect(): Rectangle {
    const a = this.$visibleArea;
    return a ? new Rectangle(a.x, a.y, a.width, a.height) : new Rectangle(0, 0, this.$stageWidth, this.$stageHeight);
  }

  /** PORT (not Flash): the part of visibleRect clear of notches, rounded corners and system bars (stage coordinates). */
  get safeRect(): Rectangle {
    const a = this.$safeArea ?? this.$visibleArea;
    return a ? new Rectangle(a.x, a.y, a.width, a.height) : new Rectangle(0, 0, this.$stageWidth, this.$stageHeight);
  }

  /**
   * PORT (not Flash): CSS px per stage px of what the screen shows (a phone in landscape ~0.7, a tablet ~1.3; 1 when no
   * host told it): what a touch target of the stage measures under a finger (48 CSS px = 48 dp).
   */
  get cssScale(): number {
    return this.$cssScale;
  }

  /**
   * @internal PORT: the host sets what the screen shows (null = the stage rectangle) and its CSS px per stage px; a
   * change dispatches Event.RESIZE on the stage (the port's layouts listen; the original game never did). Returns whether it changed.
   */
  $setView(visible: StageArea | null, safe: StageArea | null = null, cssScale: number = 1): boolean {
    const same = (a: StageArea | null, b: StageArea | null): boolean =>
      a === b || (!!a && !!b && a.x === b.x && a.y === b.y && a.width === b.width && a.height === b.height);
    // Thousandths: the backing store's rounding of a few ten-thousandths is no change, also where it crosses from one
    // thousandth to the next (0.6504 -> 0.6506 after a dynamic resolution step stays 0.650; every layout would rebuild).
    let scale = Number.isFinite(cssScale) && cssScale > 0 ? Math.round(cssScale * 1000) / 1000 : 1;
    if (Number.isFinite(cssScale) && cssScale > 0 && Math.abs(cssScale - this.$cssScale) < 0.001) scale = this.$cssScale;
    if (same(visible, this.$visibleArea) && same(safe, this.$safeArea) && scale === this.$cssScale) return false;
    this.$visibleArea = visible ? { ...visible } : null;
    this.$safeArea = safe ? { ...safe } : null;
    this.$cssScale = scale;
    this.$dispatch(new Event(Event.RESIZE));
    return true;
  }

  /** Request a RENDER broadcast before the next render (end of the current tick). */
  invalidate(): void {
    this.$invalidated = true;
  }

  get loaderInfo(): LoaderInfo | null {
    return this.$loaderInfo;
  }
}
